import { chatService } from './chatService';

export const multimediaService = {
  /**
   * Upload file with message using existing chat service
   * @param {FormData} formData - Form data containing file and user input
   * @param {string} userId - User ID
   * @param {string} timeZone - User timezone
   * @param {number} offsetMinutes - Timezone offset in minutes
   * @param {string} sessionId - Chat session ID
   * @param {boolean} newSession - Whether to create new session
   * @returns {Promise} API response
   */
  uploadFileWithMessage: async (formData, timeZone, offsetMinutes, userId, sessionId = null, newSession = false) => {
    return chatService.sendMultimodalMessage(formData, timeZone, offsetMinutes, userId, sessionId, newSession);
  },

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  validateFile: (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!file) {
      return { isValid: false, error: 'No file selected' };
    }

    if (file.size > maxSize) {
      return { isValid: false, error: 'File size must be less than 10MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'File type not supported. Please upload images, PDFs, Word documents, or text files.' };
    }

    return { isValid: true };
  },

  /**
   * Create file preview for supported file types
   * @param {File} file - File to create preview for
   * @returns {Object} File object with preview
   */
  createFilePreview: (file) => {
    const validation = multimediaService.validateFile(file);
    if (!validation.isValid) {
      console.error('File validation failed:', validation.error);
      throw new Error(validation.error);
    }

    const fileObj = {
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      preview: null,
      timestamp: new Date().toISOString()
    };

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      fileObj.preview = URL.createObjectURL(file);
    }

    return fileObj;
  },

  /**
   * Clean up file preview URLs to prevent memory leaks
   * @param {Object} fileObj - File object with preview URL
   */
  cleanupFilePreview: (fileObj) => {
    if (fileObj?.preview && fileObj.preview.startsWith('blob:')) {
      URL.revokeObjectURL(fileObj.preview);
    }
  },

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Get appropriate icon for file type
   * @param {string} fileType - MIME type of the file
   * @returns {string} Icon identifier
   */
  getFileTypeIcon: (fileType) => {
    if (fileType?.startsWith('image/')) {
      return 'image';
    } else if (fileType?.includes('pdf')) {
      return 'pdf';
    } else if (fileType?.includes('word') || fileType?.includes('document')) {
      return 'document';
    } else if (fileType?.includes('excel') || fileType?.includes('spreadsheet') || fileType?.includes('csv')) {
      return 'spreadsheet';
    } else if (fileType?.includes('text')) {
      return 'text';
    } else {
      return 'file';
    }
  }
};